import React, { useCallback, useEffect, useRef, useState } from 'react';
import { usePrefersReducedMotion } from './useMediaQuery';

/** Edit project copy here — order is carousel sequence. */
export const FLAGSHIP_PROJECTS = [
  {
    id: 1,
    domain: 'EDTECH',
    name: 'Clever Carrot',
    tagline: 'Change to learn. Learn to change.',
    summary:
      'Multi-tenant assessment platform powering national-level competitive exams with zero-tolerance uptime.',
    stack: ['.NET Core', 'Angular', 'Kubernetes', 'Azure', 'SQL Server']
  },
  {
    id: 2,
    domain: 'HEALTHCARE',
    name: 'HeartBeat',
    tagline: 'Empowering healthcare with intelligent solutions.',
    summary:
      'Hospital ERP with embedded ML for clinical decision support. HIPAA, HL7, DICOM compliant.',
    stack: ['Java', 'Spring Boot', 'ReactJS', 'React Native', 'MySQL']
  },
  {
    id: 3,
    domain: 'FINTECH',
    name: 'Altro',
    tagline: 'Build your credit history afresh.',
    summary:
      'Consumer fintech with custom multi-rail money movement and predictive ACH-risk scoring.',
    stack: ['Golang', 'Flutter', 'PostgreSQL', 'AWS']
  },
  {
    id: 4,
    domain: 'REVENUE CYCLE',
    name: 'R1 RCM',
    tagline: 'Reduce cost-to-collect. Lift patient experience.',
    summary:
      'Healthcare revenue cycle automation including consolidated billing, automated charge capture, and FHIR integrations.',
    stack: ['Java', 'React', 'Kafka', 'PostgreSQL', 'AWS']
  },
  {
    id: 5,
    domain: 'UTILITIES',
    name: 'In-Home Water Efficiency',
    tagline: 'Smarter consumption. Cleaner data.',
    summary:
      'Field-ops platform for UK water utilities with multi-tenant data cleansing, engineer routing, and offline-tolerant capture.',
    stack: ['.NET', 'React', 'React Native', 'Azure']
  },
  {
    id: 6,
    domain: 'HEALTHCARE',
    name: 'Virtual ATE',
    tagline: 'Hospital management, minus the heavyweight cost.',
    summary:
      'Lean HMS with built-in telemedicine, self-onboarding hospitals, WebRTC consults, and configurable workflows.',
    stack: ['Node.js', 'React', 'React Native', 'WebRTC']
  },
  {
    id: 7,
    domain: 'ECOMMERCE',
    name: 'Carteezy',
    tagline: 'End-to-end retail, end-to-end smart.',
    summary:
      'Retail aggregation across multiple retailers with real-time price comparison, personalised discovery, and multi-gateway checkout.',
    stack: ['Node & NestJS', 'Next.js', 'Elasticsearch', 'Redis', 'AWS']
  }
];

export const PROJECT_BLOCK_COPY = {
  blockTitle: "What we're building right now"
};

const AUTO_MS = 10000;
const RESUME_AFTER_LEAVE_MS = 3000;
const DOT_PAUSE_MS = 30000;
const MAX_VISIBLE_STACK = 4;

function padProjectNum(id) {
  return String(id).padStart(2, '0');
}

/**
 * @param {{ initialProjectId?: number; className?: string }} props
 */
export default function ProjectRotator({ initialProjectId, className = '' }) {
  const reducedMotion = usePrefersReducedMotion();
  const resolvedInitial =
    initialProjectId != null ? FLAGSHIP_PROJECTS.findIndex((p) => p.id === initialProjectId) : 0;
  const [index, setIndex] = useState(resolvedInitial >= 0 ? resolvedInitial : 0);
  const [paused, setPaused] = useState(false);
  const [playing, setPlaying] = useState(!reducedMotion);
  const leaveTimer = useRef(null);
  const dotPauseUntil = useRef(0);
  const touchStartX = useRef(null);
  const regionRef = useRef(null);

  const goTo = useCallback((nextIndex) => {
    const n = FLAGSHIP_PROJECTS.length;
    setIndex(((nextIndex % n) + n) % n);
  }, []);

  useEffect(() => {
    if (reducedMotion) {
      setPlaying(false);
      return undefined;
    }
    if (!playing || paused || Date.now() < dotPauseUntil.current) return undefined;
    const id = window.setInterval(() => {
      setIndex((i) => (i + 1) % FLAGSHIP_PROJECTS.length);
    }, AUTO_MS);
    return () => window.clearInterval(id);
  }, [playing, paused, reducedMotion, index]);

  const onPointerEnter = () => {
    if (reducedMotion) return;
    setPaused(true);
    if (leaveTimer.current) window.clearTimeout(leaveTimer.current);
  };

  const onPointerLeave = () => {
    if (reducedMotion) return;
    if (leaveTimer.current) window.clearTimeout(leaveTimer.current);
    leaveTimer.current = window.setTimeout(() => setPaused(false), RESUME_AFTER_LEAVE_MS);
  };

  const onDotClick = (i) => {
    goTo(i);
    dotPauseUntil.current = Date.now() + DOT_PAUSE_MS;
    setPaused(true);
    if (leaveTimer.current) window.clearTimeout(leaveTimer.current);
    leaveTimer.current = window.setTimeout(() => setPaused(false), DOT_PAUSE_MS);
  };

  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0]?.clientX ?? null;
  };

  const onTouchEnd = (e) => {
    const start = touchStartX.current;
    touchStartX.current = null;
    if (start == null) return;
    const end = e.changedTouches[0]?.clientX;
    if (end == null) return;
    const delta = end - start;
    if (Math.abs(delta) < 48) return;
    setIndex((i) => {
      const n = FLAGSHIP_PROJECTS.length;
      return delta < 0 ? (i + 1) % n : (i - 1 + n) % n;
    });
    dotPauseUntil.current = Date.now() + DOT_PAUSE_MS;
  };

  const project = FLAGSHIP_PROJECTS[index];
  const visibleStack = project.stack.slice(0, MAX_VISIBLE_STACK);
  const stackMore = project.stack.length - MAX_VISIBLE_STACK;

  return (
    <section
      className={`cep-block cep-project ${className}`.trim()}
      aria-labelledby="cep-project-heading"
      onMouseEnter={onPointerEnter}
      onMouseLeave={onPointerLeave}
      onFocusCapture={onPointerEnter}
      onBlurCapture={(e) => {
        if (!regionRef.current?.contains(e.relatedTarget)) onPointerLeave();
      }}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      ref={regionRef}
    >
      <div className="cep-block__head">
        <h2 id="cep-project-heading" className="cep-block__title">
          {PROJECT_BLOCK_COPY.blockTitle}
        </h2>
        <button
          type="button"
          className="cep-visually-hidden"
          onClick={() => setPlaying((p) => !p)}
          aria-pressed={playing}
        >
          {playing ? 'Pause project carousel' : 'Play project carousel'}
        </button>
      </div>

      <div
        className="cep-project__viewport"
        aria-live="polite"
        aria-atomic="true"
        aria-roledescription="carousel"
        aria-label={`Flagship project ${index + 1} of ${FLAGSHIP_PROJECTS.length}`}
      >
        <article className="cep-project__card" key={project.id}>
          <p className="cep-eyebrow">
            <span className="cep-eyebrow__rule" aria-hidden="true" />
            {padProjectNum(project.id)} / {project.domain}
          </p>
          <h3 className="cep-project__name">{project.name}</h3>
          <p className="cep-project__tagline">{project.tagline}</p>
          <p className="cep-project__summary">{project.summary}</p>
          <div className="cep-project__chips" aria-label="Technology stack">
            {visibleStack.map((tech) => (
              <span key={tech} className="cep-chip">
                {tech}
              </span>
            ))}
            {stackMore > 0 && <span className="cep-chip cep-chip--more">+{stackMore} more</span>}
          </div>
        </article>
      </div>

      <div className="cep-project__dots" role="tablist" aria-label="Choose project">
        {FLAGSHIP_PROJECTS.map((p, i) => (
          <button
            key={p.id}
            type="button"
            role="tab"
            className={`cep-project__dot${i === index ? ' cep-project__dot--active' : ''}`}
            aria-selected={i === index}
            aria-label={`Project ${p.id}: ${p.name}`}
            onClick={() => onDotClick(i)}
          />
        ))}
      </div>
    </section>
  );
}
